import { useMemo, useState } from 'react';

import { todayIso } from '@lemoment/shared';
import type { Leave } from '@lemoment/shared';

import { LeaveFormDialog } from '../../leaves';
import { LeaveListItem } from '../components';
import { useMyLeaves } from '../hooks';

const isUpcoming = (leave: Leave, today: string): boolean =>
  leave.status === 'active' && leave.endDate >= today;

const UsedAnnualCard = ({ used }: { used: number }) => (
  <section className="lift rounded-xl border border-border bg-card p-5">
    <p className="text-sm font-medium text-muted-foreground">올해 사용 연차</p>
    <p className="mt-1.5 flex items-baseline gap-1.5">
      <span className="font-numeral text-3xl font-bold tracking-tight text-lemon-ink dark:text-lemon">
        {used}
      </span>
      <span className="text-base font-medium text-muted-foreground">일</span>
    </p>
  </section>
);

interface LeaveSectionProps {
  title: string;
  count: number;
  leaves: Leave[];
  emptyText: string;
  onEdit: (leave: Leave) => void;
}

const LeaveSection = ({
  title,
  count,
  leaves,
  emptyText,
  onEdit,
}: LeaveSectionProps) => (
  <section className="space-y-3">
    <div className="flex items-baseline gap-2.5">
      <h2 className="font-display text-lg font-bold tracking-tight text-foreground">
        {title}
      </h2>
      <span className="font-numeral rounded-full bg-lemon-soft/70 px-2 py-0.5 text-xs font-bold text-lemon-ink dark:bg-lemon-soft dark:text-lemon">
        {count}
      </span>
    </div>
    {leaves.length === 0 ? (
      <p className="text-sm text-muted-foreground">{emptyText}</p>
    ) : (
      <div className="space-y-2.5">
        {leaves.map((leave) => (
          <LeaveListItem key={leave.id} leave={leave} onEdit={onEdit} />
        ))}
      </div>
    )}
  </section>
);

export const MyLeavesPage = () => {
  const { leaves, me, isLoading, isError } = useMyLeaves();
  const [editing, setEditing] = useState<Leave | undefined>(undefined);

  const { upcoming, past } = useMemo(() => {
    const today = todayIso();
    const list = leaves ?? [];
    return {
      upcoming: list
        .filter((leave) => isUpcoming(leave, today))
        .sort((a, b) => a.startDate.localeCompare(b.startDate)),
      past: list
        .filter((leave) => !isUpcoming(leave, today))
        .sort((a, b) => b.startDate.localeCompare(a.startDate)),
    };
  }, [leaves]);

  return (
    <div className="mx-auto w-full max-w-xl space-y-8 px-5 pb-32 pt-20 md:pt-12">
      {isLoading ? (
        <div role="status" className="space-y-2.5">
          <span className="sr-only">불러오는 중</span>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="skeleton h-[4.5rem] rounded-xl"
              aria-hidden
            />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">근태를 불러오지 못했습니다</p>
      ) : (
        <div className="space-y-8">
          <UsedAnnualCard used={me?.usedAnnual ?? 0} />
          <LeaveSection
            title="예정 근태"
            count={upcoming.length}
            leaves={upcoming}
            emptyText="예정된 근태가 없습니다"
            onEdit={setEditing}
          />
          <LeaveSection
            title="지난 근태"
            count={past.length}
            leaves={past}
            emptyText="지난 근태가 없습니다"
            onEdit={setEditing}
          />
        </div>
      )}

      <LeaveFormDialog
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(undefined)}
        leave={editing}
      />
    </div>
  );
};
